import { useCookieConsent } from "../hooks/useCookieConsent";
import { PrivacyPolicy, PrivacyPolicyRef } from "./PrivacyPolicy";
import { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";

// region STYLES
const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Banner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  padding: 1.5rem;
  z-index: 1001;
  animation: ${slideUp} 0.4s ease;
  will-change: transform;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Content = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  max-width: 800px;
  margin: 0 auto 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const Icon = styled.div`
  font-size: 2.75rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Text = styled.div`
  p {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    color: var(--text-secondary);
  }

  strong {
    color: var(--text-primary);
  }
`;

const Details = styled.p`
  font-size: 0.85rem !important;
  color: var(--text-secondary) !important;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button`
  font-family: var(--font-mono);
  font-size: 0.85rem;
  padding: 0.75rem 1.5rem;
  border: 1px solid;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 600;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const RejectButton = styled(Button)`
  background: transparent;
  border-color: var(--text-secondary);
  color: var(--text-secondary);

  &:hover {
    border-color: var(--text-primary);
    color: var(--text-primary);
  }
`;

const AcceptButton = styled(Button)`
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: #000;
  font-weight: 700;

  &:hover {
    background: var(--accent-hover);
    border-color: var(--accent-hover);
  }
`;

const Note = styled.p`
  text-align: center;
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 1rem;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-family: inherit;
  font-size: inherit;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--accent-color);
  }
`;
// endregion

interface CookieConsentProps {
  onAccept: () => void;
  onReject: () => void;
}

export function CookieConsent({ onAccept, onReject }: CookieConsentProps) {
  const { status, accept, reject } = useCookieConsent({ onAccept, onReject });
  const [isVisible, setIsVisible] = useState(false);
  const privacyRef = useRef<PrivacyPolicyRef>(null);

  useEffect(() => {
    if (status === "pending") {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [status]);

  const handleAccept = () => {
    accept();
    setIsVisible(false);
  };

  const handleReject = () => {
    reject();
    setIsVisible(false);
  };

  const openPrivacy = () => {
    privacyRef.current?.open();
  };

  return (
    <>
      {isVisible && (
        <Banner role="dialog" aria-label="Cookie consent">
          <Content>
            <Icon aria-hidden="true">🍪</Icon>
            <Text>
              <p>
                <strong>Hey there!</strong> This site uses cookies for analytics so we can see if anyone actually reads
                this far.
              </p>
              <Details>
                We use Google Analytics to understand visitor behavior. No personal data is sold. You can say no, we'll
                survive (probably).
              </Details>
            </Text>
          </Content>

          <Actions>
            <RejectButton onClick={handleReject}>No thanks</RejectButton>
            <AcceptButton onClick={handleAccept}>Sure, track away</AcceptButton>
          </Actions>

          <Note>
            Learn more in our <LinkButton onClick={openPrivacy}>Privacy Policy</LinkButton>
          </Note>
        </Banner>
      )}
      <PrivacyPolicy ref={privacyRef} />
    </>
  );
}
